import { useEffect, useState } from "react";
import Icon from "../../../Elements/Icon/Icon.tsx";
import Tooltip from "../../../Elements/Tooltip/Tooltip.js";
import Form from "../../../Reusable/Form/Form.js";
import Image from "../../../Elements/Image/Image.tsx";
import CustomTermsItem from "../../../Structure/Signup/CustomTermsItem.js";
import Login from "components/Structure/Login/Login.js";
import Signup from "components/Structure/Signup/Signup.js";
import replaceKeywordsWithComponents from "helpers/replaceKeywordWIthComponent.js";
import { Toggle } from "../../../Elements/Toggle/Toggle.js";
import { useContent } from "../../../../contexts/contentContext/contentContext.js";
import { currencies } from "utils/jumbomailSettings.js";
import { useCustomController } from "../../../Reusable/Form/useCustomController.js";
import useInbox from "hooks/useInbox.ts";
import { useModal } from "../../../../contexts/modalContext/modalContext.js";
import { useUser } from "../../../../contexts/userContext/userContext.js";
import { PAYMENT_ONCE_RESPONSE_ARRAY } from "utils/globalConsts.ts";
import { useToasts } from "react-toast-notifications";

const AnonUserMessage = () => {
  const { getTextByKey } = useContent();
  const LoginElem = () => {
    const { setModal } = useModal();
    const handleClick = () => {
      setModal({
        modal: Login,
        modalSize: "full-modal",
      });
    };
    return (
      <div className="clickable" onClick={handleClick}>
        {getTextByKey("login")}
      </div>
    );
  };

  const SignupElem = () => {
    const { setModal } = useModal();
    const handleClick = () => {
      setModal({
        modal: Signup,
        modalSize: "full-modal",
      });
    };
    return (
      <div className="clickable" onClick={handleClick}>
        {getTextByKey("signup")}
      </div>
    );
  };

  return (
    <div className="anon-user-message">
      <div className="anon-user-message-title">
        {replaceKeywordsWithComponents(
          getTextByKey("anon-user-message-title"),
          ["#login#", "#signup#"],
          { login: LoginElem, signup: SignupElem }
        )}
      </div>
      <div className="anon-user-message-text">
        {getTextByKey("anon-user-message-text")}
      </div>
    </div>
  );
};

export const PaymentAmount = ({ ...props }) => {
  const { getTextByKey } = useContent();
  const { isLoggedIn } = useUser();
  const { addToast } = useToasts();
  const [disableValidation, setDisableValidation] = useState(true);
  const [disabled, setDisabled] = useState(!isLoggedIn());

  const { fieldControls, hasError } = useCustomController({
    ...props,
    cutomValidation: {
      checkDisableValidation: () => disableValidation,
    },
  });

  const { body, setBody } = props.externalProps || {};

  const [charge, setCharge] = useState(fieldControls.value ? true : false);

  const [paymentValue, setPaymentValue] = useState(fieldControls.value || "");

  useEffect(() => {
    fieldControls.onChange(paymentValue);
    if (props.setBodyValue) props.setBodyValue(props, paymentValue);
  }, [paymentValue]);

  // -------- section for homepage to handle charge from another form
  // onmount - set charge from another form or if there is value in the field
  useEffect(() => {
    if (body) {
      setCharge(body.isCharge || fieldControls.value);
    }
  }, []);

  useEffect(() => {
    if (!charge) {
      setPaymentValue("");
      props.clearErrors();
      if (setBody) {
        setBody({
          ...body,
          isCharge: false,
        });
      }
    } else {
      setPaymentValue(fieldControls.value || "0");
    }
    setDisableValidation(!charge);
  }, [charge]);

  useEffect(() => {
    setDisabled(!isLoggedIn());
    if (!isLoggedIn()) setCharge(false);
  }, [isLoggedIn()]);

  const handleChangePaymentValue = (e) => {
    if (charge && !disabled) {
      // allow only numbers and dot
      // and only one dot and only 3 digits after dot
      const regex = /^[0-9]*\.?[0-9]{0,2}$/;
      if (regex.test(e.target.value) && e.target.value >= 0) {
        setPaymentValue(e.target.value);

        // logic to disable video preview if payment is enabled
        if (setBody && body.DisplayVideo) {
          setBody((prev) => ({
            ...prev,
            DisplayVideo: false,
          }));
          addToast(getTextByKey("video-disabled-for-jumbopay"), {
            appearance: "warning",
          });
        }
      }
    }
  };

  return (
    <div className="payment-options">
      {!isLoggedIn() ? <AnonUserMessage /> : null}
      <button
        type="button"
        className={`charge-my-files-container ${!disabled ? "" : " disabled"}`}
        onClick={() => {
          if (!disabled) setCharge((prev) => !prev);
        }}
        disabled={disabled}
      >
        <div className="icon-container">
          <Icon name="jpay" blue />
        </div>
        <div className="text-container">
          {getTextByKey("inbox-payment-options-title")}
          <span>{getTextByKey("charge-my-files-text")}</span>
        </div>
        <div className="flex-spacer" />
        <Toggle status={charge} setStatus={setCharge} />
      </button>
      <div className={`payment-data${charge ? "" : " disabled"}`}>
        <div className="payment-currency">{currencies.nis}</div>
        <div className={`payment-number${hasError ? " error-validation" : ""}`}>
          <input
            type="text"
            name="payment-number"
            value={paymentValue}
            disabled={!charge || disabled}
            placeholder="00.00"
            onChange={handleChangePaymentValue}
          />
        </div>
      </div>
    </div>
  );
};

const DisplayName = (props) => {
  const { getTextByKey } = useContent();
  const { fieldControls, hasError } = useCustomController(props);
  const [displayName, setDisplayName] = useState(fieldControls.value || "");

  useEffect(() => {
    fieldControls.onChange(displayName);
  }, [displayName]);

  return (
    <div className={`payment-displayname${hasError ? " invalid-input" : ""}`}>
      <div className="displayname-label">
        {getTextByKey("displayname-label-text")}
        <Tooltip
          id={"InboxPaymentOptionDisplayName"}
          tooltipTextKey={"inbox-payment-name-tooltip"}
        >
          <div className="info-icon-container">
            <Icon name="i" white />
          </div>
        </Tooltip>
      </div>
      <input
        type="text"
        name="name"
        value={displayName}
        onChange={(e) => {
          setDisplayName(e.target.value);
        }}
      />
    </div>
  );
};

const PaymentOnce = (props) => {
  const { fieldControls, hasError } = useCustomController(props);
  const [paymentOnes, setPaymentOnes] = useState(fieldControls.value);
  const { getTextByKey, getHtmlByKey } = useContent();

  useEffect(() => {
    fieldControls.onChange(paymentOnes);
  }, [paymentOnes]);

  return (
    <div className="payment-toggle-option">
      {/* <div className="option-title">{getTextByKey("extra-settings-text")}</div> */}
      <div className="option-data">
        {/* <div className="paymentonse-text">
          {getTextByKey("paymentonse-text")}
        </div> */}
        <div className="paymentonse-text">
          {getTextByKey("no-paymentonse-text")}
        </div>
        {props.tooltip ? (
          <Tooltip
            tooltipText={getHtmlByKey(props.tooltip)}
            clickable={props.clickableTooltip}
          >
            <Icon name="i" white />
          </Tooltip>
        ) : null}
        <div className="flex-spacer"></div>
        <Toggle status={!paymentOnes} setStatus={setPaymentOnes} />
      </div>
    </div>
  );
};

const EnablePreviewComponent = (props) => {
  const { fieldControls, hasError } = useCustomController(props);
  const [denyPreview, setDenyPreview] = useState(fieldControls.value);
  const { getTextByKey, getHtmlByKey } = useContent();

  useEffect(() => {
    fieldControls.onChange(denyPreview);
  }, [denyPreview]);

  return (
    <div className="payment-toggle-option">
      <div className="option-data">
        <div className="enable-preview-text">
          {getTextByKey("enable-preview-text")}
        </div>
        {props.tooltip ? (
          <Tooltip
            tooltipText={getHtmlByKey(props.tooltip)}
            clickable={props.clickableTooltip}
          >
            <Icon name="i" white />
          </Tooltip>
        ) : null}
        <div className="flex-spacer"></div>
        <Toggle status={!denyPreview} setStatus={setDenyPreview} />
      </div>
    </div>
  );
};

const paymentOptionsFields = {
  PaymentAmount: {
    element: "customelement",
    fieldKey: "PaymentAmount",
    render: (props) => <PaymentAmount {...props} />,
  },
  DisplayName: {
    element: "customelement",
    fieldKey: "DisplayName",
    validation: {
      required: true,
      reg: true,
      invalidMessage: "default-input-invalid-message",
    },
    render: (props) => <DisplayName {...props} />,
  },
  PaymentOnce: {
    element: "customelement",
    fieldKey: "PaymentOnce",
    render: (props) => (
      <PaymentOnce {...props} tooltip="jpay-request-each-user-tooltip" />
    ),
    containerClassName: "flex-start",
  },
  Preview: {
    element: "customelement",
    fieldKey: "PreviewItems",
    render: (props) => (
      <EnablePreviewComponent
        {...props}
        tooltip="jpay-enable-file-preview-tooltip"
        clickableTooltip={true}
      />
    ),
    containerClassName: "flex-start",
  },
  Terms: {
    fieldKey: "Terms",
    element: "checkbox",
    title: "Terms",
    label: "signin-terms-label",
    className: "SigninCheckbox",
    customTitleElement: CustomTermsItem,
    containerClassName: "payment-additional-option",
    validation: {
      required: true,
      reg: true,
      invalidMessage: "default-input-invalid-message",
    },
  },
};

const PaymentSettings = ({ data }) => {
  const { getTextByKey } = useContent();
  const { updateMailPayments } = useInbox();
  const { closeModal } = useModal();

  const handleSubmit = (body) => {
    return updateMailPayments(body, data.id, closeModal);
  };

  return (
    <div className="payment-settings-modal">
      <div className="payment-settings-title">
        {getTextByKey("payment-settings-modal-title")}
      </div>
      <div className="payment-settings-content">
        <Form
          fieldsProps={{ label: "hidden", field: "square" }}
          buttonProps={{ buttonsSize: "large" }}
          fields={paymentOptionsFields}
          onSubmit={handleSubmit}
          submitProps={{
            title: getTextByKey("update-settings-text"),
            className: "payment-settings-submit-button",
          }}
          defaultValuesObj={{
            PaymentOnce: PAYMENT_ONCE_RESPONSE_ARRAY.includes(
              data.payment?.PaymentType
            ),
            DisplayName: data.payment?.DisplayName,
            PaymentAmount: data.payment?.Price,
            PreviewItems: data.payment?.DenyPreview,
            Terms: true,
          }}
        ></Form>
      </div>
    </div>
  );
};
export default PaymentSettings;
